import ExcelJS from 'exceljs';
import type { ExpenseData } from '../pages/expense/types';

// ============================================================================
// 样式定义（照搬模板）
// ============================================================================

const WHITE_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFFFF' }
};

const THIN_BORDER: Partial<ExcelJS.Border> = {
  style: 'thin',
  color: { argb: 'FF000000' }
};

const ALL_BORDERS: Partial<ExcelJS.Borders> = {
  top: THIN_BORDER,
  left: THIN_BORDER,
  bottom: THIN_BORDER,
  right: THIN_BORDER
};

// 字体定义
const FONT_15: Partial<ExcelJS.Font> = { name: 'Calibri', size: 15 };
const FONT_14: Partial<ExcelJS.Font> = { name: 'Calibri', size: 14 };
const FONT_12: Partial<ExcelJS.Font> = { name: 'Calibri', size: 12 };
// 浅灰色字体 - 屏幕可见但打印时几乎看不到
const FONT_12_PLACEHOLDER: Partial<ExcelJS.Font> = { name: 'Calibri', size: 12, color: { argb: 'FFC0C0C0' } };

// 对齐方式
const CENTER_CENTER: Partial<ExcelJS.Alignment> = { horizontal: 'center', vertical: 'middle' };
const LEFT_CENTER: Partial<ExcelJS.Alignment> = { horizontal: 'left', vertical: 'middle' };
const RIGHT_CENTER: Partial<ExcelJS.Alignment> = { horizontal: 'right', vertical: 'middle' };

// ============================================================================
// 辅助函数
// ============================================================================

function setCell(
  sheet: ExcelJS.Worksheet,
  cell: string,
  value: any,
  options: {
    font?: Partial<ExcelJS.Font>;
    fill?: ExcelJS.Fill;
    alignment?: Partial<ExcelJS.Alignment>;
    border?: Partial<ExcelJS.Borders>;
    numFmt?: string;
  } = {}
) {
  const c = sheet.getCell(cell);
  c.value = value;
  if (options.font) c.font = options.font;
  if (options.fill) c.fill = options.fill;
  if (options.alignment) c.alignment = options.alignment;
  if (options.border) c.border = options.border;
  if (options.numFmt) c.numFmt = options.numFmt;
}

function setHeaderCell(
  sheet: ExcelJS.Worksheet,
  cell: string,
  value: string,
  fontSize: number = 14,
  borders: Partial<ExcelJS.Borders> = ALL_BORDERS
) {
  setCell(sheet, cell, value, {
    font: fontSize === 15 ? FONT_15 : FONT_14,
    alignment: CENTER_CENTER,
    border: borders
  });
}

function setDataCell(
  sheet: ExcelJS.Worksheet,
  cell: string,
  value: any,
  alignment: Partial<ExcelJS.Alignment> = LEFT_CENTER,
  borders: Partial<ExcelJS.Borders> = ALL_BORDERS
) {
  setCell(sheet, cell, value, {
    font: FONT_12,
    fill: WHITE_FILL,
    alignment,
    border: borders
  });
}

// 设置占位文字单元格（浅灰色，打印时几乎不可见）
function setPlaceholderCell(
  sheet: ExcelJS.Worksheet,
  cell: string,
  value: string,
  alignment: Partial<ExcelJS.Alignment> = LEFT_CENTER,
  borders: Partial<ExcelJS.Borders> = ALL_BORDERS
) {
  setCell(sheet, cell, value, {
    font: FONT_12_PLACEHOLDER,
    fill: WHITE_FILL,
    alignment,
    border: borders
  });
}

// 数字转中文大写
function numberToChinese(num: number): string {
  const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
  const units = ['', '拾', '佰', '仟'];
  const bigUnits = ['', '万', '亿'];

  if (num === 0) return '零元整';

  const yuan = Math.floor(num);
  const jiao = Math.floor((num - yuan) * 10);
  const fen = Math.round((num - yuan) * 100) % 10;

  let result = '';
  const yuanStr = yuan.toString();
  const length = yuanStr.length;

  let zeroFlag = false;
  for (let i = 0; i < length; i++) {
    const d = parseInt(yuanStr[i]);
    const pos = length - i - 1;

    if (d === 0) {
      if (pos % 4 === 0 && pos > 0) {
        const unitIdx = Math.floor(pos / 4);
        if (unitIdx < bigUnits.length && result && !result.endsWith(bigUnits[unitIdx])) {
          result += bigUnits[unitIdx];
        }
      }
      zeroFlag = true;
    } else {
      if (zeroFlag && result) {
        result += '零';
      }
      result += digits[d];
      if (pos % 4 !== 0) {
        result += units[pos % 4];
      } else if (pos > 0) {
        const unitIdx = Math.floor(pos / 4);
        if (unitIdx < bigUnits.length) {
          result += bigUnits[unitIdx];
        }
      }
      zeroFlag = false;
    }
  }

  result += '元';

  if (jiao > 0) {
    result += digits[jiao] + '角';
  }
  if (fen > 0) {
    result += digits[fen] + '分';
  } else if (jiao === 0 && fen === 0) {
    result += '整';
  }

  return result;
}

// A5 打印设置（强制压缩到一页）
function setA5PrintSettings(sheet: ExcelJS.Worksheet) {
  sheet.pageSetup = {
    paperSize: 11, // A5
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 1, // 强制压缩到一页
    horizontalCentered: true,
    margins: {
      left: 0.4,
      right: 0.4,
      top: 0.4,
      bottom: 0.4,
      header: 0.2,
      footer: 0.2
    }
  };
}

// ============================================================================
// Sheet 1: 报销单（日常/出差）
// ============================================================================

function createExpenseSheet(wb: ExcelJS.Workbook, data: ExpenseData) {
  const isDaily = data.expenseType === 'daily';
  const typeLabel = isDaily ? '日常' : '出差';
  const sheet = wb.addWorksheet(`报销单（${typeLabel}）`);
  const currencySymbol = data.currencySymbol || '¥';

  // 设置列宽（照搬模板）
  sheet.getColumn('A').width = 20.83;
  sheet.getColumn('B').width = 25.83;
  sheet.getColumn('C').width = 20.83;
  sheet.getColumn('D').width = isDaily ? 25.83 : 22.83;

  // 分组发票
  const regularInvoices = data.invoices.filter(inv => !inv.isAllowance);
  const allowanceInvoices = data.invoices.filter(inv => inv.isAllowance);

  const groupedInvoices = regularInvoices.reduce((acc, invoice) => {
    const key = invoice.type === '自定义' && invoice.customType ? invoice.customType : invoice.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(invoice);
    return acc;
  }, {} as Record<string, typeof data.invoices>);

  const summaryWithAmount = Object.keys(groupedInvoices).map(type => ({
    type,
    count: groupedInvoices[type].length,
    amount: groupedInvoices[type].reduce((sum, inv) => sum + inv.amount, 0)
  }));

  // 计算出差补贴
  let allowanceAmount = 0;
  if (!isDaily && allowanceInvoices.length > 0) {
    allowanceAmount = allowanceInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  }

  const totalAmount = data.totalAmount;
  const totalInvoices = regularInvoices.length;
  const fillDate = new Date().toLocaleDateString('zh-CN');

  // 设置行高（照搬模板）
  sheet.getRow(1).height = 30;
  sheet.getRow(2).height = 15;
  [3, 6].forEach(r => sheet.getRow(r).height = 22);
  [4, 5, 7].forEach(r => sheet.getRow(r).height = 16);

  // 动态计算后续行号
  const expenseRowStart = 8;
  const expenseRowEnd = expenseRowStart + summaryWithAmount.length - 1 + (allowanceAmount > 0 ? 1 : 0);
  const totalRowIdx = expenseRowEnd + 1;        // 合计行
  const verifyRowIdx = totalRowIdx + 1;         // 核实金额行
  const sigRowIdx = verifyRowIdx + 1;           // 审批签字标题行
  const remarkRowIdx = sigRowIdx + 3;           // 备注标题行

  // 合并单元格（照搬模板）
  sheet.mergeCells('A1:D2');
  sheet.mergeCells('A3:D3');
  sheet.mergeCells('A6:D6');
  sheet.mergeCells(`B${verifyRowIdx}:D${verifyRowIdx}`);
  sheet.mergeCells(`A${sigRowIdx}:D${sigRowIdx}`);
  sheet.mergeCells(`A${remarkRowIdx}:D${remarkRowIdx}`);
  sheet.mergeCells(`A${remarkRowIdx + 1}:D${remarkRowIdx + 2}`);

  // 填充数据
  // Row 1-2: 标题
  setHeaderCell(sheet, 'A1', '费用报销单', 15);

  // Row 3: 基本信息标题
  setHeaderCell(sheet, 'A3', '基本信息', 14);

  // Row 4: 填报时间 | 值 | 报销日期 | 值
  setDataCell(sheet, 'A4', '填报时间');
  setDataCell(sheet, 'B4', fillDate);
  setDataCell(sheet, 'C4', '报销日期');
  setPlaceholderCell(sheet, 'D4', '（待填写）');

  // Row 5: 报销人 | 值 | 报销类型 | 值
  setDataCell(sheet, 'A5', '报销人');
  setDataCell(sheet, 'B5', data.userName);
  setDataCell(sheet, 'C5', '报销类型');
  setDataCell(sheet, 'D5', `${typeLabel}报销`);

  // Row 6: 费用项目明细标题
  setHeaderCell(sheet, 'A6', '费用项目明细', 14);

  // Row 7: 表头
  setDataCell(sheet, 'A7', '费用项目', CENTER_CENTER);
  setDataCell(sheet, 'B7', '金额', CENTER_CENTER);
  setDataCell(sheet, 'C7', '发票张数', CENTER_CENTER);
  setDataCell(sheet, 'D7', '备注', CENTER_CENTER);

  // Row 8+: 费用项目数据
  let currentRow = expenseRowStart;
  summaryWithAmount.forEach(item => {
    setDataCell(sheet, `A${currentRow}`, item.type);
    setDataCell(sheet, `B${currentRow}`, `${currencySymbol}${item.amount.toFixed(2)}`, RIGHT_CENTER);
    setDataCell(sheet, `C${currentRow}`, `${item.count}张`);
    setDataCell(sheet, `D${currentRow}`, null);
    currentRow++;
  });

  // 出差补贴
  if (!isDaily && allowanceAmount > 0) {
    setDataCell(sheet, `A${currentRow}`, '出差补贴');
    setDataCell(sheet, `B${currentRow}`, `${currencySymbol}${allowanceAmount.toFixed(2)}`, RIGHT_CENTER);
    setDataCell(sheet, `C${currentRow}`, null);
    setDataCell(sheet, `D${currentRow}`, null);
  }

  // 合计行
  setDataCell(sheet, `A${totalRowIdx}`, '合计');
  setDataCell(sheet, `B${totalRowIdx}`, `${currencySymbol}${totalAmount.toFixed(2)}`, RIGHT_CENTER);
  setDataCell(sheet, `C${totalRowIdx}`, `${totalInvoices}张`);
  setDataCell(sheet, `D${totalRowIdx}`, null);

  // 核实金额行
  setDataCell(sheet, `A${verifyRowIdx}`, '核实金额（大写）');
  setDataCell(sheet, `B${verifyRowIdx}`, numberToChinese(totalAmount));

  // 审批签字标题
  setHeaderCell(sheet, `A${sigRowIdx}`, '审批签字', 14);

  // 签字区域（行高与审批签字标题一致）
  sheet.getRow(sigRowIdx).height = 19;
  sheet.getRow(sigRowIdx + 1).height = 19;
  sheet.getRow(sigRowIdx + 2).height = 19;
  setDataCell(sheet, `A${sigRowIdx + 1}`, '报销人：');
  setPlaceholderCell(sheet, `B${sigRowIdx + 1}`, '（待签字）');
  setDataCell(sheet, `C${sigRowIdx + 1}`, null);
  setDataCell(sheet, `D${sigRowIdx + 1}`, null);
  setDataCell(sheet, `A${sigRowIdx + 2}`, '审核：');
  setPlaceholderCell(sheet, `B${sigRowIdx + 2}`, '（待签字）');
  setDataCell(sheet, `C${sigRowIdx + 2}`, '出纳：');
  setPlaceholderCell(sheet, `D${sigRowIdx + 2}`, '（待签字）');

  // 备注标题
  setHeaderCell(sheet, `A${remarkRowIdx}`, '备注', 14);

  // 备注内容
  const remarkParts: string[] = [];
  if (data.projectName) remarkParts.push(`项目名称：${data.projectName}`);
  if (data.projectCode) remarkParts.push(`项目编号：${data.projectCode}`);

  if (!isDaily && allowanceInvoices.length > 0) {
    const startDates = allowanceInvoices.map(inv => inv.startDate).filter(Boolean);
    const endDates = allowanceInvoices.map(inv => inv.endDate).filter(Boolean);
    const cities = [...new Set(allowanceInvoices.map(inv => inv.city).filter(Boolean))];

    const startDate = startDates.length > 0 ? new Date(Math.min(...startDates.map(d => new Date(d!).getTime()))) : null;
    const endDate = endDates.length > 0 ? new Date(Math.max(...endDates.map(d => new Date(d!).getTime()))) : null;

    const travelParts: string[] = [];
    travelParts.push(`出发时间：${startDate ? startDate.toLocaleDateString('zh-CN', {year: 'numeric', month: 'long', day: 'numeric'}) : '未填写'}`);
    travelParts.push(`返回时间：${endDate ? endDate.toLocaleDateString('zh-CN', {year: 'numeric', month: 'long', day: 'numeric'}) : '未填写'}`);
    travelParts.push(`出差城市：${cities.length > 0 ? cities.join('、') : '未填写'}`);

    const projectLine = remarkParts.join('；');
    const travelLine = travelParts.join('；');
    const fullText = [projectLine, travelLine].filter(s => s).join('；\n');
    setCell(sheet, `A${remarkRowIdx + 1}`, fullText, {
      font: FONT_12, fill: WHITE_FILL,
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: ALL_BORDERS
    });
  } else if (remarkParts.length > 0) {
    setDataCell(sheet, `A${remarkRowIdx + 1}`, remarkParts.join('；'), CENTER_CENTER);
  } else {
    setPlaceholderCell(sheet, `A${remarkRowIdx + 1}`, '(待填写)', CENTER_CENTER);
  }

  setA5PrintSettings(sheet);
  return sheet;
}

// ============================================================================
// Sheet 2: 分项金额表（日常/出差）
// ============================================================================

function createDetailSheet(wb: ExcelJS.Workbook, data: ExpenseData) {
  const isDaily = data.expenseType === 'daily';
  const typeLabel = isDaily ? '日常' : '出差';
  const sheet = wb.addWorksheet(`分项金额表（${typeLabel}）`);
  const currencySymbol = data.currencySymbol || '¥';

  // 设置列宽（照搬模板）
  sheet.getColumn('A').width = 25.83;
  sheet.getColumn('B').width = isDaily ? 22 : 20.83;
  sheet.getColumn('C').width = isDaily ? 15 : 21.83;
  sheet.getColumn('D').width = isDaily ? 12 : 22.83;

  // 分组发票
  const regularInvoices = data.invoices.filter(inv => !inv.isAllowance);
  const allowanceInvoices = data.invoices.filter(inv => inv.isAllowance);

  const groupedInvoices = regularInvoices.reduce((acc, invoice) => {
    const key = invoice.type === '自定义' && invoice.customType ? invoice.customType : invoice.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(invoice);
    return acc;
  }, {} as Record<string, typeof data.invoices>);

  const summaryWithAmount = Object.keys(groupedInvoices).map(type => ({
    type,
    count: groupedInvoices[type].length,
    amount: groupedInvoices[type].reduce((sum, inv) => sum + inv.amount, 0),
    invoices: groupedInvoices[type]
  }));

  // 计算出差补贴
  let allowanceAmount = 0;
  let allowanceDays = 0;
  if (!isDaily && allowanceInvoices.length > 0) {
    allowanceAmount = allowanceInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    allowanceInvoices.forEach(inv => {
      if (inv.startDate && inv.endDate) {
        const start = new Date(inv.startDate);
        const end = new Date(inv.endDate);
        const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        allowanceDays = Math.max(allowanceDays, days);
      }
    });
  }

  const totalInvoices = regularInvoices.length;

  // 合并单元格
  sheet.mergeCells('A1:D2');

  // Row 1-2: 标题
  setCell(sheet, 'A1', '分项金额表', {
    font: FONT_15,
    alignment: CENTER_CENTER,
    border: ALL_BORDERS
  });

  // Row 3: 表头
  ['A3', 'B3', 'C3', 'D3'].forEach(cell => {
    const headers: Record<string, string> = { A3: '费用项目', B3: '发票号', C3: '日期', D3: '金额' };
    setCell(sheet, cell, headers[cell], {
      font: FONT_12,
      alignment: CENTER_CENTER,
      border: ALL_BORDERS
    });
  });

  // 填充数据
  let currentRow = 4;
  summaryWithAmount.forEach(typeGroup => {
    const startRow = currentRow;

    typeGroup.invoices.forEach((invoice, idx) => {
      if (idx === 0) {
        setCell(sheet, `A${currentRow}`, `${typeGroup.type} (${typeGroup.count}张，合计${currencySymbol}${typeGroup.amount.toFixed(2)}) `, {
          font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
        });
      } else {
        setCell(sheet, `A${currentRow}`, null, {
          font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
        });
      }

      setCell(sheet, `B${currentRow}`, invoice.number, {
        font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
      });
      setCell(sheet, `C${currentRow}`, invoice.date, {
        font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
      });
      setCell(sheet, `D${currentRow}`, `${currencySymbol}${invoice.amount.toFixed(2)}`, {
        font: FONT_12, fill: WHITE_FILL, alignment: RIGHT_CENTER, border: ALL_BORDERS
      });
      currentRow++;
    });

    // 合并费用类型单元格
    if (typeGroup.invoices.length > 1) {
      sheet.mergeCells(`A${startRow}:A${currentRow - 1}`);
    }
  });

  // 出差补贴
  if (!isDaily && allowanceAmount > 0) {
    const startDates = allowanceInvoices.map(inv => inv.startDate).filter(Boolean);
    const endDates = allowanceInvoices.map(inv => inv.endDate).filter(Boolean);
    const startDate = startDates.length > 0 ? new Date(Math.min(...startDates.map(d => new Date(d!).getTime()))) : null;
    const endDate = endDates.length > 0 ? new Date(Math.max(...endDates.map(d => new Date(d!).getTime()))) : null;
    const dateRange = startDate && endDate ? `${startDate.toLocaleDateString('zh-CN')} ~ ${endDate.toLocaleDateString('zh-CN')}` : '-';

    setCell(sheet, `A${currentRow}`, `出差补贴（${allowanceDays}天，合计${currencySymbol}${allowanceAmount.toFixed(0)}）`, {
      font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
    });
    setCell(sheet, `B${currentRow}`, '-', {
      font: FONT_12, fill: WHITE_FILL, alignment: CENTER_CENTER, border: ALL_BORDERS
    });
    setCell(sheet, `C${currentRow}`, dateRange, {
      font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
    });
    setCell(sheet, `D${currentRow}`, `${currencySymbol}${allowanceAmount.toFixed(2)}`, {
      font: FONT_12, fill: WHITE_FILL, alignment: RIGHT_CENTER, border: ALL_BORDERS
    });
    currentRow++;
  }

  // 合计行
  setCell(sheet, `A${currentRow}`, '合计', {
    font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
  });
  setCell(sheet, `B${currentRow}`, `${totalInvoices} 张`, {
    font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS
  });
  setCell(sheet, `C${currentRow}`, '-', {
    font: FONT_12, fill: WHITE_FILL, alignment: CENTER_CENTER, border: ALL_BORDERS
  });
  setCell(sheet, `D${currentRow}`, `${currencySymbol}${data.totalAmount.toFixed(2)}`, {
    font: FONT_12, fill: WHITE_FILL, alignment: RIGHT_CENTER, border: ALL_BORDERS
  });

  setA5PrintSettings(sheet);
  return sheet;
}

// ============================================================================
// Sheet 3: 出差申请单（仅出差报销）
// ============================================================================

function createTravelApplicationSheet(wb: ExcelJS.Workbook, data: ExpenseData) {
  const sheet = wb.addWorksheet('出差申请单（出差）');
  const currencySymbol = data.currencySymbol || '¥';

  // 设置列宽（照搬模板）
  sheet.getColumn('A').width = 22;
  sheet.getColumn('B').width = 20;
  sheet.getColumn('C').width = 20;
  sheet.getColumn('D').width = 20;

  // 分组发票
  const regularInvoices = data.invoices.filter(inv => !inv.isAllowance);
  const allowanceInvoices = data.invoices.filter(inv => inv.isAllowance);

  const groupedInvoices = regularInvoices.reduce((acc, invoice) => {
    const key = invoice.type === '自定义' && invoice.customType ? invoice.customType : invoice.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(invoice);
    return acc;
  }, {} as Record<string, typeof data.invoices>);

  const summaryWithAmount = Object.keys(groupedInvoices).map(type => ({
    type,
    count: groupedInvoices[type].length,
    amount: groupedInvoices[type].reduce((sum, inv) => sum + inv.amount, 0)
  }));

  // 提取出差日期和城市
  const startDates = allowanceInvoices.map(inv => inv.startDate).filter(Boolean);
  const endDates = allowanceInvoices.map(inv => inv.endDate).filter(Boolean);
  const startDate = startDates.length > 0 ? new Date(Math.min(...startDates.map(d => new Date(d!).getTime()))) : null;
  const endDate = endDates.length > 0 ? new Date(Math.max(...endDates.map(d => new Date(d!).getTime()))) : null;
  const cities = [...new Set(allowanceInvoices.map(inv => inv.city).filter(Boolean))];

  // 计算出差补贴
  let allowanceAmount = 0;
  if (allowanceInvoices.length > 0) {
    allowanceAmount = allowanceInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  }

  const totalAmount = data.totalAmount;
  const totalInvoices = regularInvoices.length;
  const cityRows = Math.max(5, cities.length);

  // 动态计算行号
  const costRow = 8 + cityRows;                 // 预估成本&实际开支标题行
  const costHeaderRow = costRow + 1;            // 费用项目表头行
  const costItemRow = costHeaderRow + 1;        // 费用项目数据起始行
  const bizTotalRowIdx = costItemRow + summaryWithAmount.length + (allowanceAmount > 0 ? 1 : 0); // 合计行
  const bizVerifyRowIdx = bizTotalRowIdx + 1;   // 核实金额行
  const bizSigRowIdx = bizVerifyRowIdx + 1;     // 审批签字标题行
  const bizRemarkRowIdx = bizSigRowIdx + 2;     // 备注标题行

  // 合并单元格（照搬模板）
  sheet.mergeCells('A1:D2');
  sheet.mergeCells('A3:D3');
  sheet.mergeCells('A6:D6');
  sheet.mergeCells(`A${costRow}:D${costRow}`);
  sheet.mergeCells(`B${bizVerifyRowIdx}:D${bizVerifyRowIdx}`);
  sheet.mergeCells(`A${bizSigRowIdx}:D${bizSigRowIdx}`);
  sheet.mergeCells(`A${bizRemarkRowIdx}:D${bizRemarkRowIdx}`);
  sheet.mergeCells(`A${bizRemarkRowIdx + 1}:D${bizRemarkRowIdx + 2}`);

  // 填充数据
  // Row 1-2: 标题
  setHeaderCell(sheet, 'A1', '出差申请单&费用表', 15);

  // Row 3: 基本信息标题
  setHeaderCell(sheet, 'A3', '基本信息', 14);

  // Row 4: 出差人 | 值 | 主管 | 值
  setDataCell(sheet, 'A4', '出差人');
  setDataCell(sheet, 'B4', data.userName);
  setDataCell(sheet, 'C4', '主管');
  setPlaceholderCell(sheet, 'D4', '（待填写）');

  // Row 5: 起始日期 | 值 | 结束日期 | 值
  setDataCell(sheet, 'A5', '起始日期');
  if (startDate) {
    setCell(sheet, 'B5', startDate, {
      font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS,
      numFmt: 'yyyy/mm/dd'
    });
  } else {
    setPlaceholderCell(sheet, 'B5', '（待填写）');
  }
  setDataCell(sheet, 'C5', '结束日期');
  if (endDate) {
    setCell(sheet, 'D5', endDate, {
      font: FONT_12, fill: WHITE_FILL, alignment: LEFT_CENTER, border: ALL_BORDERS,
      numFmt: 'yyyy/mm/dd'
    });
  } else {
    setPlaceholderCell(sheet, 'D5', '（待填写）');
  }

  // Row 6: 出差申请标题
  setHeaderCell(sheet, 'A6', '出差申请', 14);

  // Row 7: 表头
  setDataCell(sheet, 'A7', '拜访公司及客户', CENTER_CENTER);
  setDataCell(sheet, 'B7', ' 出差任务', CENTER_CENTER);
  setDataCell(sheet, 'C7', '项目&项目编号', CENTER_CENTER);
  setDataCell(sheet, 'D7', '目的地', CENTER_CENTER);

  // Row 8-12: 出差申请数据
  for (let i = 0; i < cityRows; i++) {
    const row = 8 + i;
    setPlaceholderCell(sheet, `A${row}`, '（待填写）');
    setPlaceholderCell(sheet, `B${row}`, '（待填写）');
    setDataCell(sheet, `C${row}`, null);
    if (i < cities.length) {
      setDataCell(sheet, `D${row}`, cities[i], CENTER_CENTER);
    } else {
      setDataCell(sheet, `D${row}`, null);
    }
  }

  // 预估成本&实际开支标题
  setHeaderCell(sheet, `A${costRow}`, '预估成本&实际开支', 14);

  // 表头
  setDataCell(sheet, `A${costHeaderRow}`, '费用项目', CENTER_CENTER);
  setDataCell(sheet, `B${costHeaderRow}`, '预估成本', CENTER_CENTER);
  setDataCell(sheet, `C${costHeaderRow}`, '实际开支', CENTER_CENTER);
  setDataCell(sheet, `D${costHeaderRow}`, '发票张数', CENTER_CENTER);

  // 费用项目数据
  let currentRow = costItemRow;
  summaryWithAmount.forEach(item => {
    setDataCell(sheet, `A${currentRow}`, item.type);
    setPlaceholderCell(sheet, `B${currentRow}`, '（待填写）');
    setDataCell(sheet, `C${currentRow}`, `${currencySymbol}${item.amount.toFixed(2)}`, RIGHT_CENTER);
    setDataCell(sheet, `D${currentRow}`, `${item.count}张`);
    currentRow++;
  });

  // 出差补贴
  if (allowanceAmount > 0) {
    setDataCell(sheet, `A${currentRow}`, '出差补贴');
    setDataCell(sheet, `B${currentRow}`, `${currencySymbol}${allowanceAmount.toFixed(2)}`, RIGHT_CENTER);
    setDataCell(sheet, `C${currentRow}`, `${currencySymbol}${allowanceAmount.toFixed(2)}`, RIGHT_CENTER);
    setDataCell(sheet, `D${currentRow}`, null);
  }

  // 合计行
  setDataCell(sheet, `A${bizTotalRowIdx}`, '合计');
  setDataCell(sheet, `B${bizTotalRowIdx}`, `${currencySymbol}${totalAmount.toFixed(2)}`, RIGHT_CENTER);
  setDataCell(sheet, `C${bizTotalRowIdx}`, `${totalInvoices}张`);
  setDataCell(sheet, `D${bizTotalRowIdx}`, null);

  // 核实金额行
  setDataCell(sheet, `A${bizVerifyRowIdx}`, '核实金额（大写）');
  setDataCell(sheet, `B${bizVerifyRowIdx}`, numberToChinese(totalAmount));

  // 审批签字标题
  setHeaderCell(sheet, `A${bizSigRowIdx}`, '审批签字', 14);

  // 签字区域（行高与审批签字标题一致）
  sheet.getRow(bizSigRowIdx).height = 19;
  sheet.getRow(bizSigRowIdx + 1).height = 19;
  setDataCell(sheet, `A${bizSigRowIdx + 1}`, '审核:');
  setPlaceholderCell(sheet, `B${bizSigRowIdx + 1}`, '（待签字）');
  setDataCell(sheet, `C${bizSigRowIdx + 1}`, '出纳');
  setPlaceholderCell(sheet, `D${bizSigRowIdx + 1}`, '（待签字）');

  // 备注标题
  setHeaderCell(sheet, `A${bizRemarkRowIdx}`, '备注', 14);

  // 备注内容
  setPlaceholderCell(sheet, `A${bizRemarkRowIdx + 1}`, '(待填写)', CENTER_CENTER);

  setA5PrintSettings(sheet);
  return sheet;
}

// ============================================================================
// 主导出函数
// ============================================================================

export const exportExpenseToExcel = async (data: ExpenseData) => {
  const workbook = new ExcelJS.Workbook();

  // 创建工作表
  createExpenseSheet(workbook, data);
  createDetailSheet(workbook, data);

  // 仅出差报销时创建出差申请单
  if (data.expenseType === 'business') {
    createTravelApplicationSheet(workbook, data);
  }

  // 生成文件名
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const filename = `报销单_${data.userName}_${timestamp}.xlsx`;

  // 生成并下载文件
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};
